import { extractAxiosErrorDetail } from '@heidi/api/errors';
import { isAxiosError } from 'axios';

/**
 * SINGLE SOURCE OF TRUTH (G1) for the BE note-push conflict detail codes
 * (`POST /projects/{id}/turns`). These are error `detail` strings, NOT part of
 * the generated `@heidi/schema` types, so they are hand-authored here and pinned
 * by `note-push-conflict-codes.test.ts` — a BE rename then fails the contract
 * test rather than silently dropping a FE branch. Every note-push conflict
 * consumer imports from here; never inline these literals elsewhere.
 */

/** 409 — the note changed under the confirmed mapping; re-fetch + re-confirm. */
export const NOTE_PUSH_CONFLICT_STALE_NOTE_REVISION =
  'note_push_plan_stale_note_revision';
/** 409 — every planned field is already verified-placed; push-complete. */
export const NOTE_PUSH_CONFLICT_ALL_FIELDS_ALREADY_PLACED =
  'note_push_plan_all_fields_already_placed';
/**
 * 409 family — the plan's flow identity no longer matches the run. The detail
 * carries a variable `:suffix` (e.g. `…:workspace`, `…:session`), so it is
 * PREFIX-matched, not compared exactly.
 */
export const NOTE_PUSH_CONFLICT_IDENTITY_MISMATCH_PREFIX =
  'note_push_plan_identity_mismatch';
/** 422 — the plan is incomplete; route to discovery (a signal, not an error). */
export const NOTE_PUSH_CONFLICT_INCOMPLETE_ROUTE_TO_DISCOVERY =
  'note_push_plan_incomplete_route_to_discovery';

/** The semantic note-push conflict a turn submit can resolve to. */
export type NotePushPlanConflict =
  | 'stale_note_revision'
  | 'all_fields_already_placed'
  | 'identity_mismatch'
  | 'incomplete_route_to_discovery';

const EXACT_409: Record<string, NotePushPlanConflict> = {
  [NOTE_PUSH_CONFLICT_STALE_NOTE_REVISION]: 'stale_note_revision',
  [NOTE_PUSH_CONFLICT_ALL_FIELDS_ALREADY_PLACED]: 'all_fields_already_placed',
};

/**
 * Classify a turn-submit failure as a note-push conflict, or `null` for anything
 * else (callers fall back to the generic submit-error surface). Keyed on the
 * machine-readable `detail` code — never the human message:
 *   - 409: the two exact codes, plus the identity-mismatch family (prefix).
 *   - 422: the incomplete → route-to-discovery signal.
 */
export const extractNotePushPlanConflict = (
  error: unknown
): NotePushPlanConflict | null => {
  if (!isAxiosError(error)) return null;
  const status = error.response?.status;
  if (status !== 409 && status !== 422) return null;
  const detail = extractAxiosErrorDetail(error);
  if (!detail) return null;
  if (status === 422) {
    return detail === NOTE_PUSH_CONFLICT_INCOMPLETE_ROUTE_TO_DISCOVERY
      ? 'incomplete_route_to_discovery'
      : null;
  }
  const exact = EXACT_409[detail];
  if (exact) return exact;
  if (
    detail === NOTE_PUSH_CONFLICT_IDENTITY_MISMATCH_PREFIX ||
    detail.startsWith(`${NOTE_PUSH_CONFLICT_IDENTITY_MISMATCH_PREFIX}:`)
  ) {
    return 'identity_mismatch';
  }
  return null;
};
